// DictionarySection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DictionarySection.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Checkbox from 'components/checkbox/Checkbox';
import TextInput from 'components/textinput/TextInput';
import TermButton from './termbutton/TermButton';

const FIELD_TYPE_ICON_KEYS = {
  activity: 'timeline',
  barcode: 'barcode',
  boolean: 'toggle_on',
  checklist: 'checklist',
  comments: 'chat',
  cover: 'image',
  currency: 'attach_money',
  date: 'calendar_today',
  daterange: 'date_range',
  datetime: 'schedule',
  datetimerange: 'event_repeat',
  description: 'description',
  enum: 'list_alt',
  location: 'location_on',
  number: 'tag',
  nodelist: 'format_list_bulleted',
  percentage: 'percent',
  people: 'group',
  richtext: 'article',
  string: 'title',
  tags: 'sell',
  text: 'short_text',
  url: 'link',
};

const getFieldType = (field) => String(field?.type || '').trim().toLowerCase();

/**
 * getFieldLabel
 * 
 * Extracts a user-friendly label for a given field, prioritizing the 'label' field, then 'name', and
 * falling back to 'Untitled Field' if neither is available or non-empty.
 * 
 * @param {object} field - The field object to extract the label from.
 * 
 * @returns {string} A string representing the field's label for display purposes.
 */
const getFieldLabel = (field) => {
  const label = String(field?.label || field?.name || '').trim();
  return label || 'Untitled Field';
};

/**
 * getFieldIconKey
 * 
 * Determines the appropriate icon key for a given field based on its type or explicit iconKey.
 * 
 * @param {object} field - The field object to determine the icon for.
 * 
 * @returns {string} The icon key corresponding to the field's type or explicit iconKey.
 */
const getFieldIconKey = (field) => {
  const explicitIcon = String(field?.iconKey || '').trim();
  if (explicitIcon) return explicitIcon;

  const type = getFieldType(field);
  return FIELD_TYPE_ICON_KEYS[type] || 'category';
};

const getFieldDescription = (field) => String(field?.fieldDescription || '').trim();

const getFieldKey = (field) => String(field?.name || field?.key || '').trim();

/**
 * getEnumOptionsSubtitle
 * 
 * Generates a subtitle string for enum fields listing their options.
 * 
 * @param {object} field - The field object to extract enum options from.
 * 
 * @return {string} A comma-separated list of enum option labels, or an empty string if not applicable.
 */
const getEnumOptionsSubtitle = (field) => {
  if (getFieldType(field) !== 'enum') return '';

  const options = Array.isArray(field?.options) ? field.options : [];
  const values = options
    .map((option) => {
      if (typeof option === 'string') return option.trim();
      if (!option || typeof option !== 'object') return '';

      const enTranslation = String(option?.translations?.en?.label || '').trim();
      if (enTranslation) return enTranslation;

      const label = String(option.label || option.value || option.name || '').trim();
      return label;
    })
    .filter(Boolean);

  return values.length ? values.join(', ') : '';
};

/**
 * DictionarySection
 *
 * Renders dictionary term and variant selection UI for provisioning.
 *
 * @param {string} query - Current dictionary search query.
 * @param {function} setQuery - Updates the dictionary search query.
 * @param {boolean} loading - Whether dictionary terms are loading.
 * @param {string} error - Optional dictionary error message.
 * @param {array} terms - Dictionary terms available for this workspace.
 * @param {object|null} selectedTerm - Selected term when viewing variants.
 * @param {object|null} selectedVariant - Selected variant for review before provisioning.
 * @param {boolean} isVariantStep - Whether the variants list is shown.
 * @param {boolean} isVariantPreviewStep - Whether the variant review step is shown.
 * @param {boolean} isProvisioning - Whether provisioning is in progress.
 * @param {string} provisioningVariantKey - Key of the variant being provisioned.
 * @param {array} selectedFieldKeys - Field keys currently selected for provisioning.
 * @param {string} workspaceKey - Selected workspace key for dictionary access.
 * @param {function} onSelectTerm - Called when a term is selected.
 * @param {function} onSelectVariant - Called when a variant is selected.
 * @param {function} onToggleSelectedField - Toggles a selected field key.
 *
 * @returns {ReactNode} The rendered component.
 */
const DictionarySection = React.memo(({
  query,
  setQuery,
  loading,
  error,
  terms,
  selectedTerm,
  selectedVariant,
  isVariantStep,
  isVariantPreviewStep,
  isProvisioning,
  provisioningVariantKey,
  selectedFieldKeys,
  workspaceKey,
  onSelectTerm,
  onSelectVariant,
  onToggleSelectedField,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();

  const renderTermRows = () => {
    if (loading) {
      return (
        <div
          className="dictionary-section-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          Loading database templates...
        </div>
      );
    }

    if (!workspaceKey) {
      return (
        <div
          className="dictionary-section-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          Select a workspace to use database templates.
        </div>
      );
    }

    if (!terms.length) {
      return (
        <div
          className="dictionary-section-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No database templates available for this workspace.
        </div>
      );
    }

    return terms.map((term) => {
      const variantCount = Array.isArray(term.variants) ? term.variants.length : 0;
      const termLabel = String(term?.labelPlural || term?.pluralLabel || '').trim() || term.label;
      return (
        <TermButton
          key={term.key}
          label={termLabel}
          variantCount={variantCount}
          isProvisioning={isProvisioning}
          onClick={() => onSelectTerm(term)}
        />
      );
    });
  };

  /**
   * renderVariantRows
   * 
   * Renders the list of variants for the selected term, sorted by selection count and title, and includes
   * provisioning state handling.
   * 
   * @returns {ReactNode} A list of variant buttons or an appropriate message if no variants are available.
   */
  const renderVariantRows = () => {
    const variants = [...(selectedTerm?.variants || [])]
      .sort((left, right) => {
        const leftCount = Number(left?.selectionCount || 0);
        const rightCount = Number(right?.selectionCount || 0);
        if (rightCount !== leftCount) return rightCount - leftCount;

        const leftTitle = String(left?.title || '').trim().toLowerCase();
        const rightTitle = String(right?.title || '').trim().toLowerCase();
        return leftTitle.localeCompare(rightTitle);
      });

    if (!variants.length) {
      return (
        <div
          className="dictionary-section-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          No variants found for this term.
        </div>
      );
    }

    return variants.map((variant) => {
      const isVariantProvisioning = isProvisioning && provisioningVariantKey === variant.key;
      const selectionCount = Number(variant?.selectionCount || 0);
      const selectionLabel = `${selectionCount} ${selectionCount === 1 ? 'usage' : 'usages'}`;
      return (
        <button
          className="dictionary-section-row"
          style={{
            color: theme.foreground,
          }}
          key={variant.key}
          disabled={isProvisioning}
          type="button"
          onClick={() => onSelectVariant(variant)}
        >
          <div className="dictionary-section-row-header">
            {variant.iconKey ? (
              <div className="dictionary-section-row-icon">
                <Icon
                  color={theme.foregroundMuted}
                  height={16}
                  iconKey={variant.iconKey}
                  width={16}
                />
              </div>
            ) : null}

            <div className="dictionary-section-row-header-content">
              <div className="dictionary-section-row-title">{variant.title}</div>
              <div
                className="dictionary-section-row-meta"
                style={{
                  color: theme.foregroundMuted,
                }}
              >
                {(variant.schema?.fields || []).length} fields • {selectionLabel}
              </div>
            </div>
          </div>
          {variant.description ? (
            <div
              className="dictionary-section-row-description"
              style={{
                color: theme.foreground,
              }}
            >
              {variant.description}
            </div>
          ) : null}
          {isVariantProvisioning ? (
            <div
              className="dictionary-section-row-meta"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              Provisioning...
            </div>
          ) : null}
        </button>
      );
    });
  };

  const renderVariantPreview = () => {
    if (!selectedVariant) {
      return (
        <div
          className="dictionary-section-empty"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          Select a variant to review fields.
        </div>
      );
    }

    const selectedFieldKeySet = new Set(Array.isArray(selectedFieldKeys) ? selectedFieldKeys : []);
    const fields = [...(selectedVariant?.schema?.fields || [])]
      .map((field, index) => ({
        field,
        index,
        fieldKey: getFieldKey(field),
        selected: selectedFieldKeySet.has(getFieldKey(field)),
      }))
      .sort((left, right) => {
        if (left.selected !== right.selected) {
          return left.selected ? -1 : 1;
        }

        return left.index - right.index;
      });
    const selectedCount = fields.filter((entry) => entry.selected).length;

    return (
      <div className="dictionary-section-preview">
        {/* PREVIEW SUMMARY */}
        <div className="dictionary-section-preview-summary">
          <div className="dictionary-section-preview-title"
            style={{
              color: theme.foreground,
            }}>
            {selectedVariant.title || 'Variant'} - Default Fields
          </div>
          {selectedVariant.description ? (
            <div
              className="dictionary-section-preview-description"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              {selectedVariant.description}
            </div>
          ) : null}
          <div
            className="dictionary-section-preview-meta"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            {selectedCount} of {fields.length} fields selected for provisioning
          </div>
        </div>

        {/* PREVIEW FIELDS */}
        <div className="dictionary-section-preview-fields">
          {!fields.length ? (
            <div
              className="dictionary-section-empty"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              This variant has no fields.
            </div>
          ) : null}

          {fields.map(({ field, fieldKey, selected, index }) => {
            const name = getFieldLabel(field);
            const fieldDescription = getFieldDescription(field);
            const enumOptionsSubtitle = getEnumOptionsSubtitle(field);
            const keySeed = fieldKey || String(field?.label || '').trim();

            return (
              <div
                className={`dictionary-section-field-row ${selected ? 'selected' : 'unselected'}`}
                key={`${keySeed || 'field'}-${index}`}
              >
                <div className="dictionary-section-field-checkbox">
                  <Checkbox
                    value={selected}
                    size={18}
                    onChange={(event) => {
                      event?.stopPropagation?.();
                      onToggleSelectedField?.(fieldKey);
                    }}
                  />
                </div>
                <div className="dictionary-section-field-icon">
                  <Icon
                    color={theme.foregroundMuted}
                    height={16}
                    iconKey={getFieldIconKey(field)}
                    width={16}
                  />
                </div>

                <div className="dictionary-section-field-content">
                  <div
                    className="dictionary-section-field-title"
                    style={{
                      color: theme.foreground,
                    }}
                  >
                    {name}
                  </div>
                  {/* 
                  <div
                    className="dictionary-section-field-subtitle"
                    style={{
                      color: theme.foregroundMuted,
                    }}
                  >
                    {typeLabel}
                  </div>
                  */}
                  {fieldDescription ? (
                    <div
                      className="dictionary-section-field-description"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                      {fieldDescription}
                    </div>
                  ) : null}
                  {enumOptionsSubtitle ? (
                    <div
                      className="dictionary-section-field-subtitle"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                      {enumOptionsSubtitle}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

            <div
              className="dictionary-section-preview-description"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              You can add, edit or delete fields after provisioning. This is just a preview of the default 
              field setup for this new database.
            </div>

      </div>
    );
  };

  return (
    <div className="dictionary-section-container">
      {/* SEARCH */}
      <div className="dictionary-section-controls">
        <TextInput
          autoFocus
          placeholder="Search available databases..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>

      {error ? (
        <div
          className="dictionary-section-error"
          style={{
            borderColor: `${theme.warning}66`,
            color: theme.warning,
          }}
        >
          {error}
        </div>
      ) : null}

      {/* LIST */}
      <div className="dictionary-section-list">
        {isVariantPreviewStep ? renderVariantPreview() : null}
        {!isVariantPreviewStep && isVariantStep ? renderVariantRows() : null}
        {!isVariantPreviewStep && !isVariantStep ? renderTermRows() : null}
      </div>
    </div>
  );
});

DictionarySection.displayName = 'DictionarySection';

export default DictionarySection;
